import { type TenantDto } from '../../services/api';
import { t } from '../../services/i18n';
import {
  ALERT_ICON,
  CALENDAR_ICON,
  canSendCandidateInvite,
  esc,
  formatDate,
  getCandidatePortalStatusConfig,
  isExpiredConverted,
  MAIL_ICON,
  NEXT_STATUS,
  PHONE_ICON,
  REFRESH_ICON,
  TRASH_ICON,
  type CandidateStatusConfig,
  getCandidateStatuses,
} from './candidate-shared';

export class RcCandidateColumn extends HTMLElement {
  status!: CandidateStatusConfig;
  candidates: TenantDto[] = [];

  private getStatusClass(statusKey: string): string {
    return `status-${statusKey.toLowerCase()}`;
  }

  render(): void {
    const visibleCandidates = this.candidates.filter((candidate) =>
      candidate.status === this.status.key && !isExpiredConverted(candidate),
    );
    const expiredCandidates = this.status.key === 'Actief'
      ? this.candidates.filter((candidate) => isExpiredConverted(candidate))
      : [];

    this.innerHTML = `
      <div class="kanban-col">
        <div class="kanban-col-header ${this.getStatusClass(this.status.key)}">
          <span>${this.status.icon}</span>
          <span>${esc(this.status.label)}</span>
          <span class="col-count">${visibleCandidates.length}</span>
        </div>
        <div class="kanban-col-body">
          ${visibleCandidates.length === 0 && expiredCandidates.length === 0
            ? `<div class="empty-col">${t('waitinglist.noCandidates')}</div>`
            : visibleCandidates.map((candidate) => this.renderCard(candidate)).join('')}
          ${expiredCandidates.length > 0 ? `
            <div class="kanban-expired-section">
              <div class="kanban-expired-label">${t('waitinglist.expiredSection', { count: expiredCandidates.length })}</div>
              ${expiredCandidates.map((candidate) => this.renderExpiredCard(candidate)).join('')}
            </div>
          ` : ''}
        </div>
      </div>
    `;

    this.querySelectorAll<HTMLElement>('[data-action="edit"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('candidate-edit', {
        bubbles: true,
        detail: { id: button.dataset.id },
      })));
    });

    this.querySelectorAll<HTMLElement>('[data-action="advance"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('candidate-status-change', {
        bubbles: true,
        detail: { id: button.dataset.id, status: button.dataset.status },
      })));
    });

    this.querySelectorAll<HTMLElement>('[data-action="reject"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('candidate-status-change', {
        bubbles: true,
        detail: { id: button.dataset.id, status: 'Afgewezen' },
      })));
    });

    this.querySelectorAll<HTMLElement>('[data-action="recandidacy"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('candidate-status-change', {
        bubbles: true,
        detail: { id: button.dataset.id, status: 'Kandidaat' },
      })));
    });

    this.querySelectorAll<HTMLElement>('[data-action="delete"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('candidate-delete', {
        bubbles: true,
        detail: { id: button.dataset.id },
      })));
    });

    this.querySelectorAll<HTMLElement>('[data-action="invite"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('candidate-invite', {
        bubbles: true,
        detail: { id: button.dataset.id },
      })));
    });
  }

  private renderExpiredCard(candidate: TenantDto): string {
    return `
      <div class="candidate-card-expired">
        <div class="card-name card-name--compact">${esc(candidate.firstName)} ${esc(candidate.lastName)}</div>
        ${candidate.email ? `<div class="card-meta card-meta--compact">
          <div class="card-meta-row">
            ${MAIL_ICON}
            ${esc(candidate.email)}</div>
        </div>` : ''}
        <div class="card-tag-expired">
          ${ALERT_ICON}
          ${t('waitinglist.expiredContract')}
        </div>
        <div class="card-actions-row">
          <button class="btn btn-outline btn-sm" data-action="recandidacy" data-id="${candidate.id}" title="${t('waitinglist.reactivateTitle')}">
            ${REFRESH_ICON}
            ${t('waitinglist.reactivateButton')}
          </button>
          <button class="btn btn-danger btn-sm" data-action="delete" data-id="${candidate.id}">
            ${TRASH_ICON}
          </button>
        </div>
      </div>
    `;
  }

  private renderCard(candidate: TenantDto): string {
    const statuses = getCandidateStatuses();
    const nextStatus = NEXT_STATUS[this.status.key];
    const nextLabel = statuses.find((status) => status.key === nextStatus)?.label;
    const isLocked = this.status.key === 'Actief';

    const advanceButton = nextStatus
      ? `<button class="btn btn-sm btn-candidate-advance ${this.getStatusClass(nextStatus)}"
           data-action="advance" data-id="${candidate.id}" data-status="${nextStatus}">${nextLabel} →</button>`
      : '';
    const rejectButton = !isLocked && this.status.key !== 'Afgewezen'
      ? `<button class="btn btn-sm btn-candidate-reject"
           data-action="reject" data-id="${candidate.id}">${t('waitinglist.rejectButton')}</button>`
      : '';
    const inviteButton = !isLocked && canSendCandidateInvite(candidate)
      ? `<button class="btn btn-outline btn-sm" data-action="invite" data-id="${candidate.id}">${candidate.invitePending ? t('waitinglist.inviteResendButton') : t('waitinglist.inviteButton')}</button>`
      : '';
    const editButton = !isLocked
      ? `<button class="btn btn-outline btn-sm" data-action="edit" data-id="${candidate.id}">${t('common.edit')}</button>`
      : '';
    const portalStatus = getCandidatePortalStatusConfig(candidate.tenantPortalConnectionStatus);

    return `
      <div class="candidate-card${isLocked ? ' card-locked' : ''}">
        <div class="card-name">${esc(candidate.firstName)} ${esc(candidate.lastName)}</div>
        <div class="card-meta">
          ${candidate.email ? `<div class="card-meta-row">
            ${MAIL_ICON}
            ${esc(candidate.email)}</div>` : ''}
          ${candidate.phone ? `<div class="card-meta-row">
            ${PHONE_ICON}
            ${esc(candidate.phone)}</div>` : ''}
          <div class="card-meta-row">
            ${CALENDAR_ICON}
            ${t('waitinglist.interestDate')}: ${formatDate(candidate.interestDate)}
          </div>
        </div>
        <div class="card-tag-expired" title="${esc(portalStatus.tooltip)}">${portalStatus.icon}${esc(portalStatus.label)}</div>
        ${candidate.notes ? `<div class="card-notes">${esc(candidate.notes)}</div>` : ''}
        <div class="card-actions-row">
          ${advanceButton}
          ${rejectButton}
          ${inviteButton}
          ${editButton}
          <button class="btn btn-danger btn-sm" data-action="delete" data-id="${candidate.id}">
            ${TRASH_ICON}
          </button>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('rc-candidate-column')) {
  customElements.define('rc-candidate-column', RcCandidateColumn);
}
